import {Game} from "../model/broadcast/game";
import {PitchStint} from "../model/broadcast/pitch-stint";

/**
 * Places the "pitches this inning / running total" pair on the scoresheet.
 *
 * The server hands us one stint per (pitcher, inning) with the batting-order
 * slot of the last batter that pitcher faced. Turning that into a cell needs
 * the offense grid's own chronological walk, because a slot can bat twice in
 * an inning (bat-around) and a half-inning does not start at slot 1.
 */

/** A cell in the offense grid, in true batting order. */
export interface ChronoCell {
  spotIdx: number;
  inningIdx: number;
  subSlot: number;
}

export interface PitchCount {
  pitches: number;
  total: number;
}

export function cellKey(cell: ChronoCell): string {
  return `${cell.spotIdx}-${cell.inningIdx}-${cell.subSlot}`;
}

/**
 * Maps stints onto cell keys. Stints arrive in game order, so the search for
 * each one starts where the previous one landed — that is what keeps a
 * bat-around from pulling a later stint back onto an earlier cell.
 *
 * `openInning` is the inning this pitching side is still working on: its stint
 * is not final, so it gets no annotation, the same as a scorer waiting for the
 * half-inning to end.
 */
export function mapPitchStintsToCells(
  stints: PitchStint[] | undefined | null,
  chrono: ChronoCell[],
  openInning?: number,
): Map<string, PitchCount> {
  const placed = new Map<string, PitchCount>();
  for (const {stint, key} of placeStints(stints, chrono)) {
    if (openInning != null && stint.inning === openInning) continue;
    if (!(stint.pitches > 0)) continue;
    placed.set(key, {pitches: stint.pitches, total: stint.total});
  }
  return placed;
}

/** One stint and the cell it belongs on. */
export interface PlacedStint {
  stint: PitchStint;
  key: string;
}

/**
 * Every stint on its cell, in game order — the walk both the pitch counts and
 * the pitching-change rule are built on.
 */
export function placeStints(
  stints: PitchStint[] | undefined | null,
  chrono: ChronoCell[],
): PlacedStint[] {
  const placed: PlacedStint[] = [];
  if (!stints?.length || !chrono.length) return placed;

  let cursor = 0;
  for (const stint of stints) {
    const spotIdx = Number(stint?.lastSpot ?? 0) - 1;
    const inningIdx = Number(stint?.inning ?? 0) - 1;
    if (inningIdx < 0) continue;

    let at = -1;
    if (spotIdx >= 0) {
      for (let i = cursor; i < chrono.length; i++) {
        if (chrono[i].inningIdx === inningIdx && chrono[i].spotIdx === spotIdx) {
          at = i;
          break;
        }
      }
    }
    // That plate appearance has no cell — the play text was one the parser
    // does not recognise, so no record was written. Fall back to the last cell
    // of the inning so the inning still shows its count.
    if (at < 0) {
      for (let i = chrono.length - 1; i >= cursor; i--) {
        if (chrono[i].inningIdx === inningIdx) {
          at = i;
          break;
        }
      }
    }
    if (at < 0) continue;
    cursor = at + 1;
    placed.push({stint, key: cellKey(chrono[at])});
  }
  return placed;
}

/**
 * The cells a pitching change is drawn under: the last plate appearance each
 * pitcher faced, except the one still on the mound when the game ended.
 *
 * Read off the stints, which carry the batting-order slot of the last batter of
 * each (pitcher, inning) as the server rebuilt it from the play log. The rule
 * this replaces counted batters faced — from `bfp`, or from innings pitched
 * plus hits, walks and hit batsmen when WBSC omits it — and that count cannot
 * see a man who reached on an error or a fielder's choice, so the line landed a
 * batter or two off wherever one of those happened. There is nothing to
 * estimate: the last batter is published.
 *
 * A run of stints rather than a set of pitchers, because the change belongs to
 * the end of each spell on the mound.
 */
export function pitcherChangeCells(
  stints: PitchStint[] | undefined | null,
  chrono: ChronoCell[],
): Set<string> {
  const changes = new Set<string>();
  const placed = placeStints(stints, chrono);
  for (let i = 0; i < placed.length - 1; i++) {
    if (placed[i].stint.pitcherGuid !== placed[i + 1].stint.pitcherGuid) {
      changes.add(placed[i].key);
    }
  }
  return changes;
}

/**
 * The inning whose pitch count is not final yet for the given pitching side —
 * the half-inning currently being played. Undefined once the game is over, or
 * between halves.
 *
 * The home team pitches the top half, the away team the bottom half.
 */
export function openStintInning(game: Game | undefined | null, pitchingSide: 'home' | 'away'): number | undefined {
  if (!game || game.finished) return undefined;
  const inning = Number(game.inning);
  if (!Number.isFinite(inning) || inning < 1) return undefined;

  switch (game.inningPart) {
    case 'T':
      return pitchingSide === 'home' ? inning : undefined;
    case 'B':
      return pitchingSide === 'away' ? inning : undefined;
    case 'M':   // top over, bottom not started
    case 'E':   // inning over
    case 'F':   // game over
      return undefined;
    default:
      // No inningPart (older docs) — fall back to which team is batting.
      return (pitchingSide === 'home' ? !game.bottom : !!game.bottom) ? inning : undefined;
  }
}
